import { type FC, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteIcon from "@mui/icons-material/Delete";
import PublishIcon from "@mui/icons-material/Publish";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { Player } from "@/shared/components/Player";
import { useUpdateContentMutation } from "@/partner/entities/contents";
import { useDeleteStreamClipMutation } from "@/partner/entities/streams";

interface StreamClipListProps {
  clips: {
    startSeconds: number;
    durationSeconds: number;
    // Мгновенное превью поверх записи эфира — не null только для клипов
    // короче 60 секунд (потолок instant clipping у Cloudflare), и не зависит
    // от того, готово ли основное видео (см. StreamClip.previewUrl).
    previewUrl?: string | null;
    downloadUrl?: string | null;
    content: {
      id: string;
      title: string;
      publishAt?: string | null;
      // Клип — не загруженное Image, у него никогда нет preview: оставлено
      // только как фолбэк на случай будущих не-стримовых клипов.
      preview?: { url?: string | null } | null;
      // Реален только когда клип уже готов (Content.playback резолвится
      // строго от videoId, который выставляет VideoService.promote) — то
      // есть ровно тогда же, когда появляется content.video.
      playback?: { thumbnailUrl?: string | null } | null;
      // Реален независимо от готовности видео — VideoService.applyStatusUpdate
      // выставляет его на самом Content, в отличие от video.transcodingError,
      // которое структурно недостижимо (video появляется только readyToStream).
      transcodingError?: string | null;
      video?: { readyToStream: boolean } | null;
    };
  }[];
  onChanged: () => void;
}

export const StreamClipList: FC<StreamClipListProps> = ({
  clips,
  onChanged,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftTitle, setDraftTitle] = useState("");

  const [updateContent] = useUpdateContentMutation({
    onError: (error) => enqueueSnackbar(error.message, { variant: "error" }),
  });
  const [deleteStreamClip] = useDeleteStreamClipMutation({
    onError: (error) => enqueueSnackbar(error.message, { variant: "error" }),
  });

  const saveTitle = async (id: string) => {
    const { data } = await updateContent({
      variables: { id, input: { title: draftTitle } },
    });
    setEditingId(null);
    if (!data) return;
    // Переиспользуем сообщение видео-редактора: клип — обычное видео, и
    // это тот же useUpdateContentMutation, что и в EditVideoDialog.
    // streams.studio.createdSnackbar здесь не подходит — это "Клип создан",
    // а тут просто переименование.
    enqueueSnackbar(t("videos.edit.success"), {
      variant: "success",
    });
    onChanged();
  };

  const publish = async (id: string) => {
    const { data } = await updateContent({
      variables: { id, input: { publishAt: new Date().toISOString() } },
    });
    if (!data) return;
    enqueueSnackbar(t("streams.studio.publishedSnackbar"), {
      variant: "success",
    });
    onChanged();
  };

  const remove = async (id: string) => {
    if (!window.confirm(t("streams.studio.confirmDelete"))) return;
    const { data } = await deleteStreamClip({ variables: { contentId: id } });
    if (!data) return;
    enqueueSnackbar(t("streams.studio.deletedSnackbar"), {
      variant: "success",
    });
    onChanged();
  };

  if (!clips.length) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
        {t("streams.studio.clipsEmpty")}
      </Typography>
    );
  }

  return (
    <Stack spacing={1}>
      <Typography variant="subtitle1">
        {t("streams.studio.clipsTitle")}
      </Typography>
      {clips.map((clip) => {
        const { content } = clip;
        const ready = Boolean(content.video?.readyToStream);
        const failed = Boolean(content.transcodingError);
        // playback.thumbnailUrl — фрейм самого клипа, но резолвится (как и
        // content.video) только когда клип уже готов; preview — фолбэк на
        // случай не-стримовых клипов, у сегодняшних его никогда нет.
        const thumbnailUrl =
          content.playback?.thumbnailUrl ?? content.preview?.url ?? null;
        // Пока клип не готов (и не упал), мгновенное превью — единственный
        // способ его посмотреть: без него партнёр видит только чип
        // "Обрабатывается" и ждёт транскодирования вслепую, хотя весь смысл
        // двух-механизмного дизайна (см. спеку, цель №4) — в обратном.
        const showPreviewPlayer = !ready && !failed && Boolean(clip.previewUrl);
        return (
          <Card key={content.id} elevation={0}>
            <CardContent
              sx={{ display: "flex", gap: 1.5, alignItems: "center" }}
            >
              {showPreviewPlayer ? (
                <Box sx={{ width: 160, flexShrink: 0 }}>
                  <Player
                    src={clip.previewUrl}
                    poster={thumbnailUrl ?? undefined}
                    hotkeys={false}
                  />
                </Box>
              ) : (
                thumbnailUrl && (
                  <Box
                    component="img"
                    src={thumbnailUrl}
                    alt=""
                    sx={{ width: 96, borderRadius: 1, flexShrink: 0 }}
                  />
                )
              )}
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                {editingId === content.id ? (
                  <TextField
                    value={draftTitle}
                    onChange={(event) => setDraftTitle(event.target.value)}
                    size="small"
                    fullWidth
                    autoFocus
                    onKeyDown={(event) => {
                      if (event.key === "Enter") void saveTitle(content.id);
                    }}
                  />
                ) : (
                  <Typography noWrap>{content.title}</Typography>
                )}
                <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    {clip.durationSeconds}s
                  </Typography>
                  {failed ? (
                    <Chip
                      size="small"
                      label={t("streams.studio.failed")}
                      color="error"
                    />
                  ) : (
                    !ready && (
                      <Chip
                        size="small"
                        label={t("streams.studio.processing")}
                        color="warning"
                      />
                    )
                  )}
                </Stack>
              </Box>
              <Stack direction="row">
                {editingId === content.id ? (
                  <IconButton onClick={() => void saveTitle(content.id)}>
                    <CheckIcon />
                  </IconButton>
                ) : (
                  <Tooltip title={t("streams.studio.edit")}>
                    <IconButton
                      onClick={() => {
                        setEditingId(content.id);
                        setDraftTitle(content.title);
                      }}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                )}
                {!content.publishAt && ready && (
                  <Tooltip title={t("streams.studio.publish")}>
                    <IconButton onClick={() => void publish(content.id)}>
                      <PublishIcon />
                    </IconButton>
                  </Tooltip>
                )}
                {clip.downloadUrl && (
                  <Tooltip title={t("streams.studio.download")}>
                    <IconButton
                      component="a"
                      href={clip.downloadUrl}
                      target="_blank"
                      rel="noopener"
                    >
                      <DownloadIcon />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title={t("streams.studio.delete")}>
                  <IconButton onClick={() => void remove(content.id)}>
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </Stack>
            </CardContent>
          </Card>
        );
      })}
    </Stack>
  );
};
